import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

export default function AssignTaskModal({ open, onClose, onSaved, defaultDate }) {
  const [executives, setExecutives] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    date: defaultDate || "",
    time: "10:00",
    location: "",
    assigned_to_user_id: "",
    campaign_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setForm(f => ({ ...f, date: defaultDate || f.date, title: "", description: "", location: "" }));
    (async () => {
      try {
        const [e, c] = await Promise.all([
          api.get("/marketing/my-executives"),
          api.get("/campaigns"),
        ]);
        setExecutives(e.data);
        setCampaigns(c.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, [open, defaultDate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.assigned_to_user_id || !form.title || !form.date) {
      setErr("Title, date and assignee are required");
      return;
    }
    setSaving(true);
    try {
      const scheduled_at = `${form.date}T${form.time || "10:00"}:00`;
      await api.post("/tasks", {
        title: form.title.trim(),
        description: form.description,
        scheduled_at,
        location: form.location,
        assigned_to_user_id: form.assigned_to_user_id,
        campaign_id: form.campaign_id || null,
      });
      toast.success("Objective assigned — executive notified");
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="assign-task-modal">
      <div className="w-full max-w-lg iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500">
          <X className="w-4 h-4"/>
        </button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Assign Objective</p>
        <h2 className="font-display text-2xl text-white mb-6">Delegate a field task</h2>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Assign To</label>
            <select value={form.assigned_to_user_id} onChange={(e) => setForm(f => ({...f, assigned_to_user_id: e.target.value}))} className="iz-input" required data-testid="assign-executive">
              <option value="">Select assignee…</option>
              {executives.map(x => <option key={x.id} value={x.id}>{x.name}{x.role_name ? ` · ${x.role_name}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Title</label>
            <input value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} className="iz-input" placeholder="e.g. Site visit at Willow Park" data-testid="assign-title" required/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Description</label>
            <textarea value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} className="iz-input" rows={3} data-testid="assign-desc"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm(f => ({...f, date: e.target.value}))} className="iz-input" required data-testid="assign-date"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Time</label>
              <input type="time" value={form.time} onChange={(e) => setForm(f => ({...f, time: e.target.value}))} className="iz-input" data-testid="assign-time"/>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Location</label>
            <input value={form.location} onChange={(e) => setForm(f => ({...f, location: e.target.value}))} className="iz-input" placeholder="Address or landmark" data-testid="assign-location"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Campaign (optional)</label>
            <select value={form.campaign_id} onChange={(e) => setForm(f => ({...f, campaign_id: e.target.value}))} className="iz-input" data-testid="assign-campaign">
              <option value="">None</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="assign-submit">
              {saving ? "Assigning…" : "Assign & Notify"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
